import { useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import useDialog from "../../../utils/hooks/useDialog";
import {
  createTimetableEntry,
  updateTimetableEntry,
  deleteTimetableEntry,
} from "../../../services/timetableService";
import { PERIOD_CHOICES } from "../../../utils/constants/observations";
import { runErrorMessage } from "../../../components/timetable/runErrors";

const WEEKDAY_KEY = { 1: "mon", 2: "tue", 3: "wed", 4: "thu", 5: "fri", 6: "sat", 0: "sun" };

// Add / edit / remove one of the teacher's OWN recurring lessons. weekday is
// fixed to the cell the teacher clicked; class, period and start time are editable.
export default function LessonEditorModal({ entry, weekday, period, groups, onClose, onSaved }) {
  const { t } = useTranslation();
  const isEdit = Boolean(entry);
  const [groupUuid, setGroupUuid] = useState(entry?.group?.uuid || "");
  const [periodValue, setPeriodValue] = useState(entry?.period || period || 1);
  const [startTime, setStartTime] = useState((entry?.start_time || "08:00").slice(0, 5));
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const dayKey = WEEKDAY_KEY[weekday] || "mon";

  // Escape + focus trap + scroll lock; dismissal blocked while saving.
  const dialogRef = useDialog(busy ? () => {} : onClose);

  const save = async () => {
    if (!groupUuid) {
      setError(t("schedule.classRequired"));
      return;
    }
    setError("");
    setBusy(true);
    const body = { group: groupUuid, weekday, period: periodValue, start_time: startTime };
    try {
      if (isEdit) {
        await updateTimetableEntry({ uuid: entry.uuid, body });
      } else {
        await createTimetableEntry({ body });
      }
      toast.success(t("schedule.lessonSaved"));
      onSaved();
      onClose();
    } catch (err) {
      // Was a regex over the server's ENGLISH prose — which runErrors.js names
      // as the thing not to do, since it mislabels every conflict type the
      // backend grows next — wrapped around `data.period[0]`, which indexes a
      // CHARACTER out of that prose whenever the field is a bare string.
      setError(runErrorMessage(err, t, t("schedule.lessonSaveFailed")));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await deleteTimetableEntry({ uuid: entry.uuid });
      toast.success(t("schedule.lessonRemoved"));
      onSaved();
      onClose();
    } catch (err) {
      setError(runErrorMessage(err, t, t("schedule.lessonSaveFailed")));
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4"
      onClick={() => !busy && onClose()}
    >
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="lesson-editor-title"
        tabIndex={-1}
        className="relative bg-white rounded-[32px] shadow-2xl w-full max-w-md overflow-hidden border border-slate-200 focus:outline-none"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
          <h3 id="lesson-editor-title" className="text-lg font-black text-slate-900">
            {isEdit ? t("schedule.editLesson") : t("schedule.addLesson")}
            <span className="ml-2 text-[10px] font-black text-indigo-500 uppercase tracking-widest">
              {t(`upcoming.${dayKey}`)}
            </span>
          </h3>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full hover:bg-slate-200 flex items-center justify-center text-slate-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
            aria-label={t("common.close")}
          >
            <svg aria-hidden="true" className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="space-y-2">
            <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">
              {t("schedule.lessonGroupLabel")}
            </label>
            <select
              value={groupUuid}
              onChange={(e) => {
                setGroupUuid(e.target.value);
                setError("");
              }}
              aria-invalid={error && !groupUuid ? "true" : undefined}
              aria-describedby={error ? "lesson-error" : undefined}
              className={`w-full px-4 py-3 bg-slate-50 border rounded-2xl text-sm font-bold text-slate-900 outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 ${
                error && !groupUuid ? "border-rose-400 ring-1 ring-rose-300" : "border-slate-200"
              }`}
            >
              <option value="">{t("schedule.pickClass")}</option>
              {groups.map((g) => (
                <option key={g.uuid} value={g.uuid}>
                  {g.name}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">
                {t("schedule.lessonPeriodLabel")}
              </label>
              <select
                value={periodValue}
                onChange={(e) => setPeriodValue(Number(e.target.value))}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold text-slate-900 outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500"
              >
                {PERIOD_CHOICES.map((p) => (
                  <option key={p} value={p}>
                    P{p}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">
                {t("schedule.lessonTimeLabel")}
              </label>
              <input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold text-slate-900 outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500"
              />
            </div>
          </div>

          {error && (
            <p
              id="lesson-error"
              role="alert"
              className="text-[11px] font-black text-rose-600 bg-rose-50 border border-rose-100 rounded-xl px-3 py-2"
            >
              {error}
            </p>
          )}

          <div className="flex items-center justify-end gap-3 pt-2">
            {isEdit && (
              <button
                onClick={remove}
                disabled={busy}
                className="mr-auto px-4 py-3 bg-white border border-rose-200 text-rose-600 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-rose-50 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
              >
                {t("schedule.removeLesson")}
              </button>
            )}
            <button
              onClick={save}
              disabled={busy}
              className={`${isEdit ? "px-8" : "flex-1"} py-3 bg-indigo-600 text-white rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-indigo-700 shadow-lg shadow-indigo-200 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2`}
            >
              {t("schedule.saveLesson")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
